// client/src/coms/views/chatview_parts/ChatMessageRow.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { renderTextWithLinks } from './chatTextRender';
import { ReactionEmoji, STICKER_MESSAGE_RE, StickerImage } from '../../customMedia';
import AnnouncementAck from '../../serveradmin/AnnouncementAck';
import InteractionCard from '../../interactions/InteractionCard';
import { EncryptedPlaceholder, RestoredNote } from '../../e2ee/EncryptedMessageView';

// Messages posted by the server itself: automations, safety alerts, event reminders, announcements.
const SYSTEM_BADGES = { automation: 'Automation', safety: 'Safety', event: 'Event', announcement: 'Announcement', introduction: 'Introduction', e2ee: 'Encryption' };

const WHAT_SHALL_WE_PLAY_PREFIX = '[vox-what-shall-we-play-v1]';
const WHAT_SHALL_WE_PLAY_RESULTS_PREFIX = '[vox-what-shall-we-play-results-v1]';

function parseWhatShallWePlayMessage(text) {
  const raw = String(text || '');
  if (!raw.startsWith(WHAT_SHALL_WE_PLAY_PREFIX)) return null;

  const jsonText = raw.slice(WHAT_SHALL_WE_PLAY_PREFIX.length).trim();
  if (!jsonText) return null;

  try {
    const parsed = JSON.parse(jsonText);
    if (!parsed || typeof parsed !== 'object') return null;

    return {
      version: 1,
      type: 'what-shall-we-play',
      sessionId: String(parsed.sessionId || '').trim(),
      serverId: String(parsed.serverId || '').trim(),
      channelId: String(parsed.channelId || '').trim(),
      channelDbId: String(parsed.channelDbId || '').trim(),
      channelName: String(parsed.channelName || '').trim(),
      startedByUserId: String(parsed.startedByUserId || '').trim(),
      startedByName: String(parsed.startedByName || '').trim(),
      createdAt: String(parsed.createdAt || '').trim(),
      prompt: String(parsed.prompt || 'Choose the games you would play.').trim(),
      ctaLabel: String(parsed.ctaLabel || 'Choose games').trim(),
    };
  } catch {
    return null;
  }
}

function parseWhatShallWePlayResultsMessage(text) {
  const raw = String(text || '');
  if (!raw.startsWith(WHAT_SHALL_WE_PLAY_RESULTS_PREFIX)) return null;

  const jsonText = raw.slice(WHAT_SHALL_WE_PLAY_RESULTS_PREFIX.length).trim();
  if (!jsonText) return null;

  try {
    const parsed = JSON.parse(jsonText);
    if (!parsed || typeof parsed !== 'object') return null;
    if (String(parsed.type || '').trim() !== 'what-shall-we-play-results') return null;

    const topGames = Array.isArray(parsed.topGames)
      ? parsed.topGames
          .map((game, index) => {
            const appid = Number(game && game.appid);
            const votes = Number(game && game.votes);
            const name = String(game && game.name ? game.name : `Game ${index + 1}`).trim();

            if (!name) return null;

            return {
              appid: Number.isFinite(appid) && appid > 0 ? appid : null,
              name,
              votes: Number.isFinite(votes) && votes > 0 ? Math.floor(votes) : 0,
            };
          })
          .filter(Boolean)
      : [];

    return {
      version: 1,
      type: 'what-shall-we-play-results',
      sessionId: String(parsed.sessionId || '').trim(),
      serverId: String(parsed.serverId || '').trim(),
      channelId: String(parsed.channelId || '').trim(),
      channelDbId: String(parsed.channelDbId || '').trim(),
      createdAt: String(parsed.createdAt || '').trim(),
      windowSeconds: Number.isFinite(Number(parsed.windowSeconds))
        ? Math.max(0, Math.floor(Number(parsed.windowSeconds)))
        : 0,
      totalVoters: Number.isFinite(Number(parsed.totalVoters))
        ? Math.max(0, Math.floor(Number(parsed.totalVoters)))
        : 0,
      topGames,
    };
  } catch {
    return null;
  }
}

function dispatchWhatShallWePlayOpen(payload) {
  try {
    window.dispatchEvent(new CustomEvent('vox-what-shall-we-play-open', { detail: payload || {} }));
  } catch {
    try {
      window.dispatchEvent(new Event('vox-what-shall-we-play-open'));
    } catch {}
  }
}

function WhatShallWePlayCard({ payload }) {
  const startedByName = String(payload && payload.startedByName ? payload.startedByName : '').trim();
  const channelName = String(payload && payload.channelName ? payload.channelName : '').trim();
  const prompt = String(
    payload && payload.prompt ? payload.prompt : 'Choose the games you would play.'
  ).trim();
  const ctaLabel = String(
    payload && payload.ctaLabel ? payload.ctaLabel : 'Choose games'
  ).trim() || 'Choose games';

  return (
    <div
      style={{
        display: 'grid',
        gap: 10,
        padding: 14,
        borderRadius: 14,
        background: 'linear-gradient(180deg, rgba(var(--vxs-4-rgb, 34,39,51), 0.95) 0%, rgba(var(--vxs-3-rgb, 24,28,38), 0.98) 100%)',
        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
        boxShadow: '0 10px 26px rgba(0,0,0,0.22)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <div
          aria-hidden="true"
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          🎮
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2 }}>What shall we play?</div>
          <div style={{ fontSize: 12, opacity: 0.72, marginTop: 2 }}>
            {startedByName ? `${startedByName} started this` : 'Play session started'}
            {channelName ? ` in ${channelName}` : ''}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 13, lineHeight: 1.45, opacity: 0.9 }}>
        {prompt || 'Choose the games you would play.'}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            dispatchWhatShallWePlayOpen(payload);
          }}
          style={{
            appearance: 'none',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            color: 'var(--vx-text, #fff)',
            padding: '10px 14px',
            borderRadius: 10,
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}

function WhatShallWePlayResultsCard({ payload }) {
  const totalVoters = Math.max(
    0,
    Number.isFinite(Number(payload && payload.totalVoters))
      ? Math.floor(Number(payload.totalVoters))
      : 0
  );

  const topGames = Array.isArray(payload && payload.topGames) ? payload.topGames : [];
  const highestVotes = topGames.reduce((max, game) => {
    const votes = Number.isFinite(Number(game && game.votes)) ? Math.floor(Number(game.votes)) : 0;
    return votes > max ? votes : max;
  }, 0);

  const hasGames = topGames.length > 0;

  return (
    <div
      style={{
        display: 'grid',
        gap: 12,
        padding: 14,
        borderRadius: 14,
        background: 'linear-gradient(180deg, rgba(var(--vxs-4-rgb, 34,39,51), 0.95) 0%, rgba(var(--vxs-3-rgb, 24,28,38), 0.98) 100%)',
        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
        boxShadow: '0 10px 26px rgba(0,0,0,0.22)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <div
          aria-hidden="true"
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          🏆
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2 }}>What shall we play?</div>
          <div style={{ fontSize: 12, opacity: 0.72, marginTop: 2 }}>
            {totalVoters === 1 ? '1 voter' : `${totalVoters} voters`}
          </div>
        </div>
      </div>

      {hasGames ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {topGames.map((game, index) => {
            const votes = Math.max(
              0,
              Number.isFinite(Number(game && game.votes)) ? Math.floor(Number(game.votes)) : 0
            );
            const name = String(game && game.name ? game.name : `Game ${index + 1}`).trim();
            const fillPct = highestVotes > 0 ? Math.max(6, Math.round((votes / highestVotes) * 100)) : 0;

            return (
              <div
                key={`${String(game && game.appid ? game.appid : '')}-${name}-${index}`}
                style={{
                  display: 'grid',
                  gap: 6,
                  padding: '9px 10px',
                  borderRadius: 10,
                  background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.045 * var(--vx-ov, 1)))',
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                    gap: 10,
                    alignItems: 'center',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </div>

                  <div
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                    title={name}
                  >
                    {name}
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      opacity: 0.9,
                      flexShrink: 0,
                    }}
                  >
                    {votes}
                  </div>
                </div>

                <div
                  aria-hidden="true"
                  style={{
                    width: '100%',
                    height: 8,
                    borderRadius: 999,
                    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${fillPct}%`,
                      height: '100%',
                      borderRadius: 999,
                      background: 'linear-gradient(90deg, rgba(151,120,255,0.95) 0%, rgba(93,166,255,0.95) 100%)',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            opacity: 0.85,
          }}
        >
          No votes were submitted.
        </div>
      )}
    </div>
  );
}

function splitLeadingQuoteBlock(text) {
  const raw = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = raw.split('\n');

  let i = 0;
  const quoteLines = [];
  while (i < lines.length) {
    const line = String(lines[i] || '');
    if (!/^\s*>/.test(line)) break;

    const stripped = line.replace(/^\s*>\s?/, '');
    quoteLines.push(stripped);
    i += 1;
  }

  if (!quoteLines.length) return { quoteText: '', restText: raw };

  const quoteText = quoteLines.join('\n');

  const restLines = lines.slice(i);
  while (restLines.length && String(restLines[0] || '').trim() === '') restLines.shift();
  const restText = restLines.join('\n');

  return { quoteText, restText };
}

function splitQuoteHeaderAndBody(quoteText) {
  const raw = String(quoteText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = raw.split('\n');

  while (lines.length && String(lines[0] || '').trim() === '') lines.shift();
  if (!lines.length) return { header: '', body: '' };

  if (lines.length === 1) {
    return { header: '', body: String(lines[0] || '') };
  }

  const header = String(lines[0] || '').trim();

  const bodyLines = lines.slice(1);
  while (bodyLines.length && String(bodyLines[0] || '').trim() === '') bodyLines.shift();
  const body = bodyLines.join('\n');

  return { header, body };
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

function safeFirstUrlFromText(text) {
  const s = String(text || '');
  const m = s.match(/https?:\/\/[^\s<>()]+/i);
  if (!m || !m[0]) return '';
  const raw = String(m[0]).trim();
  try {
    return new URL(raw).toString();
  } catch {
    return raw;
  }
}

function classifyMediaUrl(url) {
  const u = String(url || '').trim().toLowerCase();
  if (!u) return { kind: '', url: '' };

  const clean = u.split('#')[0].split('?')[0];

  const isImage =
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(clean) ||
    clean.includes('i.imgur.com/') ||
    clean.includes('media.discordapp.net/') ||
    clean.includes('cdn.discordapp.com/');

  const isVideo = /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(clean);

  const isYouTube =
    clean.includes('youtube.com/watch') ||
    clean.includes('youtu.be/') ||
    clean.includes('youtube.com/shorts/');

  if (isImage) return { kind: 'image', url };
  if (isVideo) return { kind: 'video', url };
  if (isYouTube) return { kind: 'youtube', url };
  return { kind: '', url: '' };
}

function buildReactionTooltip(reaction) {
  const r = reaction && typeof reaction === 'object' ? reaction : {};
  const emoji = String(r.emoji || '').trim();
  const users = safeArray(r.users)
    .map((u) => {
      const user = u && typeof u === 'object' ? u : {};
      return String(user.username || '').trim();
    })
    .filter(Boolean);

  if (!emoji) return '';
  if (!users.length) return emoji;

  return `${emoji} reacted: ${users.join(', ')}`;
}

function getPinnedActionStyle(actionId, active) {
  if (!active) return null;

  const key = String(actionId || '').trim().toLowerCase();
  const redActionIds = new Set([
    'pin',
    'super-pin',
    'super_pin',
    'superpin',
    'motd',
    'header',
    'header-message',
    'header_message',
  ]);

  if (!redActionIds.has(key)) return null;

  return {
    background: 'linear-gradient(180deg, #ff5a5a 0%, #d81f3a 100%)',
    color: '#fff',
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.2 * var(--vx-ov, 1)))',
    boxShadow: '0 0 0 1px rgba(255,80,80,0.35), 0 0 14px rgba(255,50,50,0.45)',
    transform: 'scale(1.06)',
  };
}

function normalizeMessageFont(value) {
  const raw = String(value || '').trim();

  switch (raw) {
    case 'Arial':
      return 'Arial, Helvetica, sans-serif';
    case 'Georgia':
      return 'Georgia, "Times New Roman", serif';
    case 'Courier New':
      return '"Courier New", Courier, monospace';
    case 'Comic Sans MS':
      return '"Comic Sans MS", "Comic Sans", cursive';
    case 'inherit':
    default:
      return 'inherit';
  }
}

export default function ChatMessageRow({
  m,
  i,
  isShellMobile,
  avatarWrapperStyle,
  messagesCount,
  editingMessageId,
  editDraft,
  editBusyMessageId,
  deleteBusyMessageId,
  hoveredMessageId,
  setHoveredMessageId,
  setEditDraft,
  beginEditMessage,
  deleteMessage,
  cancelEditMessage,
  saveEditMessage,
  quoteMessage,
  messageActions = [],
}) {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const hoverLeaveTimerRef = useRef(null);

  useEffect(() => {
    setAvatarFailed(false);
  }, [m && m.avatarUrl]);

  useEffect(() => {
    return () => {
      if (hoverLeaveTimerRef.current) {
        clearTimeout(hoverLeaveTimerRef.current);
        hoverLeaveTimerRef.current = null;
      }
    };
  }, []);

  const isEditingThis = String(editingMessageId || '') === String((m && m.id) || '');
  const editBusyThis = String(editBusyMessageId || '') === String((m && m.id) || '');
  const deleteBusyThis = String(deleteBusyMessageId || '') === String((m && m.id) || '');
  const isHoveredThis = String(hoveredMessageId || '') === String((m && m.id) || '');

  const compact = !!m.groupedWithPrev && !isEditingThis;
  // End-to-end encrypted messages this device cannot show keep their row, with a placeholder (EncryptedMessageView).
  const e2eeView = (m && m.raw && m.raw.e2ee_view) || null;
  const e2eeHidden = !!e2eeView && e2eeView.kind !== 'ok' && e2eeView.kind !== 'restored';

  const whatShallWePlay = useMemo(() => parseWhatShallWePlayMessage(m && m.text), [m && m.text]);
  const whatShallWePlayResults = useMemo(
    () => parseWhatShallWePlayResultsMessage(m && m.text),
    [m && m.text]
  );

  const { quoteText, restText } = splitLeadingQuoteBlock(
    whatShallWePlay || whatShallWePlayResults ? '' : m && m.text
  );

  // A message that is exactly <sticker:ID> is shown as the sticker image.
  const stickerMatch = STICKER_MESSAGE_RE.exec(String((m && m.text) || '').trim());
  const stickerId = stickerMatch ? stickerMatch[1] : '';

  const quoteParts = splitQuoteHeaderAndBody(quoteText);
  const quoteHeaderLine = String(quoteParts.header || '').trim();
  const quoteBody = String(quoteParts.body || '');

  const bodyUrl = quoteBody ? safeFirstUrlFromText(quoteBody) : '';
  const bodyMedia = classifyMediaUrl(bodyUrl);

  const bodyIsJustUrl =
    !!bodyUrl &&
    !!quoteBody &&
    quoteBody.replace(/\s+/g, '') === bodyUrl.replace(/\s+/g, '');

  const avatarInitial = String(m.author || '?').trim().charAt(0).toUpperCase() || '?';
  const hasAvatar = !!String(m && m.avatarUrl ? m.avatarUrl : '').trim() && !avatarFailed;
  const messageFontFamily = normalizeMessageFont(m && m.font);

  const reactions = useMemo(() => {
    return safeArray(m && m.reactions)
      .map((reaction) => {
        const r = reaction && typeof reaction === 'object' ? reaction : {};
        const emoji = String(r.emoji || '').trim();
        const count = Math.max(0, toInt(r.count, 0));
        const reacted = !!r.reacted;
        const users = safeArray(r.users).map((u) => {
          const user = u && typeof u === 'object' ? u : {};
          return {
            id: toInt(user.id, 0),
            username: String(user.username || '').trim(),
          };
        });
        if (!emoji || count <= 0) return null;
        return {
          emoji,
          count,
          reacted,
          users,
        };
      })
      .filter(Boolean);
  }, [m]);

  const visibleActions = useMemo(() => {
    const ctx = {
      editingMessageId,
      editBusyMessageId,
      deleteBusyMessageId,
      hoveredMessageId,
      isEditingThis,
      isHoveredThis,
      isShellMobile,
      beginEditMessage,
      deleteMessage,
      quoteMessage,
    };

    return (Array.isArray(messageActions) ? messageActions : []).filter((action) => {
      if (!action || typeof action !== 'object') return false;
      if (typeof action.show === 'function') return !!action.show(m, ctx);
      return true;
    });
  }, [
    m,
    messageActions,
    editingMessageId,
    editBusyMessageId,
    deleteBusyMessageId,
    hoveredMessageId,
    isEditingThis,
    isHoveredThis,
    isShellMobile,
    beginEditMessage,
    deleteMessage,
    quoteMessage,
  ]);

  const hasActionSlot = visibleActions.length > 0;
  const showActionButtons = hasActionSlot && (isHoveredThis || isEditingThis);
  const actionSlotWidth =
    visibleActions.length <= 1 ? 34 : visibleActions.length === 2 ? 80 : visibleActions.length * 40;

  function onRowClick() {
    if (!isShellMobile) return;
    if (isEditingThis) return;
    setHoveredMessageId((prev) => (String(prev || '') === String(m.id || '') ? '' : m.id));
  }

  function onRowMouseEnter() {
    if (isShellMobile) return;
    if (hoverLeaveTimerRef.current) {
      clearTimeout(hoverLeaveTimerRef.current);
      hoverLeaveTimerRef.current = null;
    }
    setHoveredMessageId(m.id);
  }

  function onRowMouseLeave() {
    if (isShellMobile) return;
    if (isEditingThis) return;

    if (hoverLeaveTimerRef.current) {
      clearTimeout(hoverLeaveTimerRef.current);
      hoverLeaveTimerRef.current = null;
    }

    hoverLeaveTimerRef.current = setTimeout(() => {
      setHoveredMessageId((prev) => (String(prev || '') === String(m.id || '') ? '' : prev));
      hoverLeaveTimerRef.current = null;
    }, 1000);
  }

  function stopRowToggle(e) {
    if (!e) return;
    e.stopPropagation();
  }

  function resolveActionValue(value, fallback = '') {
    if (typeof value === 'function') {
      try {
        return value(m, {
          editingMessageId,
          editBusyMessageId,
          deleteBusyMessageId,
          hoveredMessageId,
          isEditingThis,
          isHoveredThis,
          isShellMobile,
        });
      } catch {
        return fallback;
      }
    }
    return value == null ? fallback : value;
  }

  function renderActions() {
    return visibleActions.map((action, idx) => {
      const disabled = typeof action.disabled === 'function' ? !!action.disabled(m) : false;
      const active =
        typeof action.active === 'function'
          ? !!action.active(m, {
              editingMessageId,
              editBusyMessageId,
              deleteBusyMessageId,
              hoveredMessageId,
            })
          : false;

      const resolvedTitle = String(
        resolveActionValue(action.title, resolveActionValue(action.label, '')) || ''
      );
      const resolvedLabel = String(resolveActionValue(action.label, '') || '');
      const resolvedIcon = resolveActionValue(action.icon, resolvedLabel || '•');
      const pinnedActiveStyle = getPinnedActionStyle(action && action.id, active);

      return (
        <button
          key={String(action.id || resolvedLabel || idx)}
          type="button"
          title={resolvedTitle}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            if (typeof action.onClick === 'function') action.onClick(m, e);
          }}
          style={{
            appearance: 'none',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            background: active ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))' : 'rgba(var(--vxs-2-rgb, 18,22,32), 0.92)',
            color: 'var(--vx-text, #fff)',
            width: 34,
            height: 30,
            borderRadius: 10,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.55 : 1,
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1,
            transition: 'opacity 120ms ease, transform 120ms ease, background 120ms ease',
            ...(pinnedActiveStyle || {}),
          }}
        >
          {resolvedIcon}
        </button>
      );
    });
  }

  function renderInlineReactions() {
    if (!reactions.length) return null;

    return (
      <div
        style={{
          display: 'inline-flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {reactions.map((reaction, idx) => (
          <span
            key={`${reaction.emoji}-${idx}`}
            title={buildReactionTooltip(reaction)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '2px 8px',
              borderRadius: 999,
              background: reaction.reacted ? 'rgba(var(--vx-link-rgb, 110,160,255), 0.16)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.07 * var(--vx-ov, 1)))',
              border: reaction.reacted
                ? '1px solid rgba(var(--vx-link-rgb, 110,160,255), 0.28)'
                : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
              fontSize: 12,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>
              <ReactionEmoji emoji={reaction.emoji} size={16} />
            </span>
            <span style={{ opacity: 0.88, fontWeight: 700 }}>{reaction.count}</span>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div
      data-message-id={String(m && m.id ? m.id : '')}
      id={`coms-message-${String(m && m.id ? m.id : '')}`}
      className="coms-msg"
      onClick={onRowClick}
      onMouseEnter={onRowMouseEnter}
      onMouseLeave={onRowMouseLeave}
      style={{
        display: 'flex',
        gap: compact ? 8 : 10,
        alignItems: 'flex-start',
        paddingTop: compact ? 3 : 10,
        paddingBottom: compact ? 3 : i === messagesCount - 1 ? 10 : 8,
        cursor: isShellMobile && !isEditingThis ? 'pointer' : 'default',
      }}
    >
      <div
        style={{
          ...(avatarWrapperStyle || {}),
          ...(compact
            ? {
                opacity: 0,
                pointerEvents: 'none',
              }
            : {}),
        }}
      >
        {hasAvatar ? (
          <img
            src={m.avatarUrl}
            alt={m.author}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            onError={() => {
              setAvatarFailed(true);
            }}
          />
        ) : (
          <span>{avatarInitial}</span>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        {hasActionSlot ? (
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 3,
              width: actionSlotWidth,
              display: 'flex',
              justifyContent: 'flex-end',
              pointerEvents: showActionButtons ? 'auto' : 'none',
            }}
            onClick={stopRowToggle}
            onMouseEnter={() => {
              if (isShellMobile) return;
              if (hoverLeaveTimerRef.current) {
                clearTimeout(hoverLeaveTimerRef.current);
                hoverLeaveTimerRef.current = null;
              }
              setHoveredMessageId(m.id);
            }}
            onMouseLeave={() => {
              if (isShellMobile) return;
              if (isEditingThis) return;

              if (hoverLeaveTimerRef.current) {
                clearTimeout(hoverLeaveTimerRef.current);
                hoverLeaveTimerRef.current = null;
              }

              hoverLeaveTimerRef.current = setTimeout(() => {
                setHoveredMessageId((prev) => (String(prev || '') === String(m.id || '') ? '' : prev));
                hoverLeaveTimerRef.current = null;
              }, 1000);
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                gap: 6,
                alignItems: 'center',
                justifyContent: 'flex-end',
                minWidth: actionSlotWidth,
                opacity: showActionButtons ? 1 : 0,
                transition: 'opacity 120ms ease',
              }}
            >
              {renderActions()}
            </span>
          </div>
        ) : null}

        <div
          style={{
            minWidth: 0,
            paddingRight: hasActionSlot && !isShellMobile ? actionSlotWidth + 10 : 0,
          }}
        >
          {!compact ? (
            <div
              className="coms-msg-user"
              style={{
                ...(isShellMobile ? { lineHeight: 1.2 } : undefined),
                display: 'flex',
                alignItems: isShellMobile ? 'flex-start' : 'center',
                gap: 8,
                minWidth: 0,
                flexWrap: isShellMobile ? 'wrap' : 'nowrap',
                overflow: isShellMobile ? 'visible' : 'hidden',
                minHeight: 18,
              }}
            >
              <strong style={{ flexShrink: 0 }}>{m.author}</strong>
              {m.systemKind && SYSTEM_BADGES[m.systemKind] ? (
                <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', padding: '0 6px', borderRadius: 4, background: 'rgba(var(--vx-accent-rgb, 88,101,242), 0.35)', color: 'var(--vx-text, #fff)' }}>
                  {SYSTEM_BADGES[m.systemKind]}
                </span>
              ) : null}
              {m.raw && m.raw.encryption === 'e2ee' ? (
                <span title="Sent end-to-end encrypted" aria-label="End-to-end encrypted message" data-testid="msg-e2ee" style={{ flexShrink: 0, fontSize: 11, opacity: 0.8 }}>
                  🔒
                </span>
              ) : null}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minWidth: 0,
                  overflow: isShellMobile ? 'visible' : 'hidden',
                  whiteSpace: isShellMobile ? 'normal' : 'nowrap',
                  flex: isShellMobile ? '1 0 100%' : 1,
                  flexWrap: isShellMobile ? 'wrap' : 'nowrap',
                  paddingLeft: isShellMobile ? 0 : undefined,
                }}
              >
                {m.stamp ? (
                  <span
                    style={{
                      opacity: 0.75,
                      fontSize: 12,
                      lineHeight: 1.2,
                      flexShrink: 0,
                    }}
                  >
                    {m.stamp}
                  </span>
                ) : null}

                {m.edited ? (
                  <span
                    style={{
                      opacity: 0.6,
                      fontSize: 12,
                      lineHeight: 1.2,
                      flexShrink: 0,
                    }}
                  >
                    (edited)
                  </span>
                ) : null}

                {renderInlineReactions()}
              </div>
            </div>
          ) : !isEditingThis && (m.edited || reactions.length > 0) ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 0,
                overflow: isShellMobile ? 'visible' : 'hidden',
                whiteSpace: isShellMobile ? 'normal' : 'nowrap',
                flexWrap: isShellMobile ? 'wrap' : 'nowrap',
                minHeight: 16,
                marginBottom: 2,
              }}
            >
              {m.edited ? (
                <span
                  style={{
                    opacity: 0.6,
                    fontSize: 12,
                    lineHeight: 1.2,
                    flexShrink: 0,
                  }}
                >
                  (edited)
                </span>
              ) : null}

              {renderInlineReactions()}
            </div>
          ) : null}

          {isEditingThis ? (
            <div style={{ display: 'grid', gap: 8, marginTop: compact ? 0 : 6 }}>
              <textarea
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  resize: 'vertical',
                  background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                  borderRadius: 12,
                  padding: 12,
                  color: 'var(--vx-text, #fff)',
                  boxSizing: 'border-box',
                }}
              />

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => saveEditMessage(m.id)}
                  disabled={editBusyThis || !String(editDraft || '').trim()}
                  style={{
                    appearance: 'none',
                    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
                    color: 'var(--vx-text, #fff)',
                    padding: '7px 11px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  Save
                </button>

                <button
                  type="button"
                  onClick={cancelEditMessage}
                  style={{
                    appearance: 'none',
                    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                    color: 'var(--vx-text, #fff)',
                    padding: '7px 11px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : e2eeHidden ? (
            <div className="coms-msg-text" style={{ marginTop: compact ? 0 : 6 }}>
              <EncryptedPlaceholder view={e2eeView} />
            </div>
          ) : m.systemKind === 'interaction' ? (
            <div className="coms-msg-text" style={{ marginTop: compact ? 0 : 2 }}>
              <InteractionCard messageId={m.id} fallbackText={String(m.text || '')} />
            </div>
          ) : whatShallWePlay ? (
            <div
              className="coms-msg-text"
              style={{
                marginTop: compact ? 0 : 6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: messageFontFamily,
              }}
            >
              <WhatShallWePlayCard payload={whatShallWePlay} />
            </div>
          ) : whatShallWePlayResults ? (
            <div
              className="coms-msg-text"
              style={{
                marginTop: compact ? 0 : 6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: messageFontFamily,
              }}
            >
              <WhatShallWePlayResultsCard payload={whatShallWePlayResults} />
            </div>
          ) : (
            <div
              className="coms-msg-text"
              style={{
                marginTop: compact ? 0 : 6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: messageFontFamily,
              }}
            >
              {quoteText ? (
                <div
                  style={{
                    marginBottom: restText ? 8 : 0,
                    padding: '8px 10px',
                    borderRadius: 10,
                    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                    borderLeft: '3px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1)))',
                    fontFamily: messageFontFamily,
                  }}
                >
                  {quoteHeaderLine ? (
                    <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.75, marginBottom: quoteBody ? 4 : 0 }}>
                      {quoteHeaderLine}
                    </div>
                  ) : null}

                  {quoteBody ? (
                    <div style={{ opacity: 0.88, fontFamily: messageFontFamily }}>
                      {bodyMedia.kind === 'image' && bodyIsJustUrl ? (
                        <img
                          src={bodyMedia.url}
                          alt=""
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: 260,
                            borderRadius: 10,
                          }}
                        />
                      ) : bodyMedia.kind === 'video' && bodyIsJustUrl ? (
                        <video
                          src={bodyMedia.url}
                          controls
                          playsInline
                          preload="metadata"
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: 320,
                            borderRadius: 10,
                          }}
                        />
                      ) : (
                        renderTextWithLinks(quoteBody, { embeds: Number(m.suppress_embeds) !== 1 })
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {stickerId ? (
                <StickerImage id={stickerId} />
              ) : (
                <>
                  {restText ? renderTextWithLinks(restText, { embeds: Number(m.suppress_embeds) !== 1 }) : null}
                  {!quoteText && !restText ? renderTextWithLinks(String(m.text || ''), { embeds: Number(m.suppress_embeds) !== 1 }) : null}
                  {m.systemKind === 'announcement' ? <AnnouncementAck messageId={m.id} /> : null}
                  {e2eeView && e2eeView.kind === 'restored' ? <RestoredNote /> : null}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}